import { MockExamStudent } from '@models/interfaces/mock_exam.interface';
import { Button, DropdownFilter, Input, Snackbar } from 'geniebook-ui/dist';
import React, {
  createRef,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import './StudentForm.css';
import {
  MOCK_EXAM_LEVEL_NAME,
  MOCK_EXAM_LEVEL_OPTIONS,
} from '@utils/constants/mock-exam.constants';
import mockExamSplitName from '@utils/helpers/mockExamSplitName';
import Trash from '@assets/icons/trash';
const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const StudentForm = ({
  students,
  setStudents,
  loading,
  setLoading,
  redirectToBookSession,
  isExistingParent,
}: {
  students: MockExamStudent[];
  setStudents: React.Dispatch<React.SetStateAction<MockExamStudent[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  redirectToBookSession: () => void;
  isExistingParent: boolean;
}) => {
  const [deletedStudents, setDeletedStudents] = useState<MockExamStudent[]>([]);
  const [permanentDeleteKey, setPermanentDeleteKey] = useState<string>('');
  const [dropdownRefs, setDropdownRefs] = React.useState<
    RefObject<HTMLDivElement>[]
  >([]);
  const studentListContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollToBottom, setIsScrollToBottom] = useState<boolean>(false);
  const [viewWidth, setViewWidth] = useState<number>(0);
  useEffect(() => {
    window.addEventListener('resize', () => setViewWidth(window.innerWidth));
    setViewWidth(window.innerWidth);
    return () => {
      window.removeEventListener('resize', () =>
        setViewWidth(window.innerWidth),
      );
    };
  }, []);
  useEffect(() => {
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    for (let i = 0; i < newStudents.length; i++) {
      newStudents[i].levelPopupOpen = false;
    }
    setStudents(newStudents);

    setDropdownRefs(dropdownRefs =>
      Array(newStudents.length)
        .fill(null)
        .map((_, i) => dropdownRefs[i] || createRef()),
    );
  }, []);

  const onSubmit = () => {
    setLoading(true);
    let isError = false;
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    for (let i = 0; i < newStudents.length; i++) {
      if (newStudents[i].student_id === null) {
        if (
          newStudents[i].student_name === undefined ||
          newStudents[i].student_name?.trim() === ''
        ) {
          isError = true;
          newStudents[i].fullNameError = 'Full Name is required';
        } else {
          newStudents[i].fullNameError = '';
        }

        if (
          newStudents[i].level_id === parseInt(MOCK_EXAM_LEVEL_OPTIONS[0].value)
        ) {
          isError = true;
          newStudents[i].levelError = 'Level is required';
        }
      }
    }
    setStudents(newStudents);
    if (!isError) redirectToBookSession();
    setLoading(false);
  };

  useEffect(() => {
    setDropdownRefs(dropdownRefs =>
      Array(students.length)
        .fill(null)
        .map((_, i) => dropdownRefs[i] || createRef()),
    );
    const handleClickOutside = (event: MouseEvent) => {
      const newStudents: MockExamStudent[] = JSON.parse(
        JSON.stringify(students),
      );
      //   setStudents(newStudents);
      for (let i = 0; i < dropdownRefs.length; i++) {
        if (
          dropdownRefs[i].current &&
          !dropdownRefs[i].current?.contains(event.target as Node)
        ) {
          newStudents[i].levelPopupOpen = false;
        }
      }
      setStudents(newStudents);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [students]);

  const handleFullNameOnChange = (fullName: string, studentIndex: number) => {
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    fullName = fullName.substring(0, 60);
    const arrName = mockExamSplitName(fullName);
    newStudents[studentIndex].student_name = fullName;
    newStudents[studentIndex].first_name = arrName.first_name;
    newStudents[studentIndex].last_name = arrName.last_name;
    setStudents(newStudents);
  };

  const handleChangePopupOpen = (
    studentIndex: number,
    value: boolean | null,
  ) => {
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    if (value !== null) newStudents[studentIndex].levelPopupOpen = value;
    else
      newStudents[studentIndex].levelPopupOpen =
        !newStudents[studentIndex].levelPopupOpen;
    setStudents(newStudents);

    if (
      newStudents.length >= 5 &&
      studentIndex !== newStudents.length - 1 &&
      studentListContainerRef.current !== null &&
      dropdownRefs[studentIndex] !== null &&
      dropdownRefs[studentIndex].current !== null
    ) {
      const dropdownPopupHeight = 162;
      const dropdownPos =
        (dropdownRefs[studentIndex]?.current?.offsetTop ?? 0) -
        studentListContainerRef.current.scrollTop;
      const dropdownPopupPos =
        dropdownPos +
        (dropdownRefs[studentIndex]?.current?.clientHeight ?? 0) +
        2; // 2 for gap beetween dropdown and popup
      if (
        dropdownPopupPos + dropdownPopupHeight >
        studentListContainerRef.current.clientHeight
      ) {
        const diff =
          dropdownPopupPos +
          dropdownPopupHeight -
          studentListContainerRef.current.clientHeight;
        studentListContainerRef.current.scrollTop += diff + 10; // 10 for safety
      }
    }
  };

  const handleChangeLevel = (levelID: string, studentIndex: number) => {
    const newStudents: MockExamStudent[] = JSON.parse(JSON.stringify(students));
    const levelName = MOCK_EXAM_LEVEL_NAME.filter(
      el => el.id === parseInt(levelID),
    );
    if (
      parseInt(newStudents[studentIndex].level_id + '') !== parseInt(levelID)
    ) {
      newStudents[studentIndex].classes = [];
    }

    newStudents[studentIndex].level_id = parseInt(levelID);
    newStudents[studentIndex].level_name = levelName[0].name;
    newStudents[studentIndex].levelPopupOpen = false;
    setStudents(newStudents);
  };

  const handleAddStudent = () => {
    if (students.length < 5) {
      const newStudents: MockExamStudent[] = JSON.parse(
        JSON.stringify(students),
      );
      const newStudent: MockExamStudent = {
        student_id: null,
        first_name: '',
        last_name: '',
        student_name: '',
        level_id: parseInt(MOCK_EXAM_LEVEL_OPTIONS[0].value + ''),
        level_name: MOCK_EXAM_LEVEL_OPTIONS[0].label,
        classes: [],
        savedClasses: [],
        checked: true,
        levelPopupOpen: false,
        fullNameError: '',
        levelError: '',
      };
      newStudents.push(newStudent);
      setStudents(newStudents);
      setIsScrollToBottom(true);
    }
  };

  useEffect(() => {
    if (isScrollToBottom) {
      if (studentListContainerRef.current) {
        const child = studentListContainerRef.current.querySelectorAll('div');
        if (child.length > 0)
          studentListContainerRef.current.scrollTop = child[0].clientHeight;
      }
      setIsScrollToBottom(false);
    }
  }, [isScrollToBottom]);

  const onDeleteStudent = (studentIndex: number) => {
    const newStudents: MockExamStudent[] = [];
    students.forEach((student: MockExamStudent, index: number) => {
      if (studentIndex !== index) newStudents.push(student);
    });

    const newDeletedStudents: MockExamStudent[] = JSON.parse(
      JSON.stringify(deletedStudents),
    );
    const deleteKey = makeid();
    students[studentIndex].deleteKey = deleteKey;
    newDeletedStudents.push(students[studentIndex]);

    setStudents(newStudents);
    setDeletedStudents(newDeletedStudents);

    setTimeout(() => {
      setPermanentDeleteKey(deleteKey);
    }, 5000);
  };

  const onUndoDeleteStudent = (deletedStudentKey: string) => {
    const newDeletedStudents: MockExamStudent[] = deletedStudents.filter(
      el => el.deleteKey !== deletedStudentKey,
    );
    const filterStudent = deletedStudents.filter(
      el => el.deleteKey === deletedStudentKey,
    );
    if (filterStudent.length > 0) {
      const deletedStudent = filterStudent[0];
      const newStudents: MockExamStudent[] = JSON.parse(
        JSON.stringify(students),
      );
      deletedStudent.deleteKey = undefined;
      newStudents.push(deletedStudent);

      setStudents(newStudents);
      setDeletedStudents(newDeletedStudents);
    }
  };

  function makeid(length: number = 50) {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  useEffect(() => {
    if (permanentDeleteKey !== '') {
      const newDeletedStudents: MockExamStudent[] = deletedStudents.filter(
        el => el.deleteKey !== permanentDeleteKey,
      );
      setDeletedStudents(newDeletedStudents);
    }
  }, [permanentDeleteKey]);

  const isFormError = () => {
    let isError = false;
    if (students.length > 0) {
      const newStudents: MockExamStudent[] = JSON.parse(
        JSON.stringify(students),
      );
      for (let i = 0; i < newStudents.length; i++) {
        if (
          newStudents[i].student_id === null &&
          (newStudents[i].student_name === undefined ||
            newStudents[i].student_name === '' ||
            newStudents[i].level_id ===
              parseInt(MOCK_EXAM_LEVEL_OPTIONS[0].value))
        ) {
          isError = true;
        }
      }
    } else isError = true;

    return isError;
  };

  const isEnrolled = (student: MockExamStudent) => {
    return (
      isExistingParent &&
      student.savedClasses !== undefined &&
      student.savedClasses.length > 0
    );
  };

  const isPopupLevelOpened = () => {
    let isOpened = false;
    for (let i = 0; i < students.length && !isOpened; i++) {
      if (students[i].levelPopupOpen) isOpened = true;
    }
    return isOpened;
  };

  return (
    <>
      <div className="relative pb-[108px]">
        <form className="mb-0" onSubmit={onSubmit}>
          <div
            className={
              'mock-exam-student-form-list no-scroll' +
              (isPopupLevelOpened() ? ' level-popup-open' : '') +
              (students.length > 0 &&
              students[students.length - 1].levelPopupOpen
                ? ' last-level-popup-open'
                : '') +
              (students.length >= 5 ? ' max-limit-student' : '')
            }
            ref={studentListContainerRef}
          >
            <div>
              {students.map(
                (student: MockExamStudent, studentIndex: number) => {
                  return (
                    <div
                      className={
                        'mock-exam-student-row' +
                        (isEnrolled(student) ? ' enrolled' : '')
                      }
                      key={studentIndex}
                    >
                      <div className="flex justify-start items-center">
                        <p className="uppercase text-neutral-dust text-xs font-black opacity-50">
                          Child {studentIndex + 1}
                        </p>
                        {student.student_id === null && (
                          <Trash
                            className="fill-current text-primary-apricot cursor-pointer ml-auto w-[14px] h-4 hover:text-alert-error"
                            onClick={() => onDeleteStudent(studentIndex)}
                          />
                        )}
                        {isEnrolled(student) && (
                          <div className="mock-exam-student-list-enrolled">
                            Enrolled
                          </div>
                        )}
                      </div>
                      {student.student_id === null ? (
                        <>
                          <div className="flex justify-center items-start mt-4">
                            <div className="w-full">
                              <Input
                                mode={'light'}
                                variant={'outside'}
                                placeholder="Enter child's full name"
                                className="student-form-input"
                                label="Full Name"
                                error={student.fullNameError}
                                value={student.student_name}
                                onChange={(
                                  e: React.FormEvent<HTMLInputElement>,
                                ) => {
                                  handleFullNameOnChange(
                                    e.currentTarget.value,
                                    studentIndex,
                                  );
                                }}
                              />
                            </div>
                          </div>
                          <div
                            className={
                              'w-full mt-6 ' +
                              (student.levelPopupOpen
                                ? 'text-primary-apricot'
                                : 'text-primary-navyblue')
                            }
                          >
                            <p className="font-medium mb-2">Level</p>
                            <div
                              onClick={() => {
                                handleChangePopupOpen(studentIndex, null);
                              }}
                              ref={dropdownRefs[studentIndex]}
                              className=""
                            >
                              <DropdownFilter
                                classNameBase={
                                  'mock-exam-student-form-dropdown w-full h-12 !text-neutral-pencil !bg-white ' +
                                  (student.levelPopupOpen
                                    ? 'open'
                                    : '!border-neutral-notebook') +
                                  (student.level_id + '' ===
                                  MOCK_EXAM_LEVEL_OPTIONS[0].value
                                    ? ' placeholder'
                                    : '')
                                }
                                classNameOption="!text-neutral-pencil !bg-white z-10 !top-[3.1rem] shadow-md sketchbook-hover-options mock-exam-student-form-level-option"
                                setSelected={(value: string | number) => {
                                  handleChangeLevel(value + '', studentIndex);
                                }}
                                options={MOCK_EXAM_LEVEL_OPTIONS}
                                label={''}
                                selected={student.level_id + ''}
                              />
                              {student.levelError !== '' && (
                                <span className="leading-3.5 pt-2 text-alert-error text-sm">
                                  {student.levelError}
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className={
                              ((
                                student.first_name +
                                ' ' +
                                student.last_name
                              ).trim().length <= 15
                                ? 'flex justify-start items-center'
                                : '') +
                              (isEnrolled(student) ? ' mt-[21px]' : ' mt-4')
                            }
                          >
                            <p
                              className={
                                (isEnrolled(student)
                                  ? 'text-neutral-dust opacity-50'
                                  : 'text-neutral-pencil') +
                                ((
                                  student.first_name +
                                  ' ' +
                                  student.last_name
                                ).trim().length > 15
                                  ? ' mock-exam-student-name-truncate'
                                  : '')
                              }
                            >
                              {(
                                student.first_name +
                                ' ' +
                                student.last_name
                              ).trim()}
                            </p>
                            <p
                              className={
                                'text-neutral-dust text-sm ' +
                                ((
                                  student.first_name +
                                  ' ' +
                                  student.last_name
                                ).trim().length <= 15
                                  ? 'ml-2 '
                                  : '') +
                                (isEnrolled(student) ? 'opacity-50' : '')
                              }
                            >
                              {student.level_name}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          </div>

          <div
            className={
              'mock-exam-student-form-button-container' +
              (students.length >= 5 ? ' max-limit-student' : '')
            }
          >
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={isFormError() || loading}
              className="max-w-full"
            >
              Continue
            </Button>
            {students.length < 5 && (
              <Button
                variant="secondary"
                onClick={handleAddStudent}
                disabled={loading}
                className="max-w-full mb-4 mt-3"
              >
                + Add another child
              </Button>
            )}
          </div>
        </form>
      </div>
      {deletedStudents.map((deletedStudent: MockExamStudent) => {
        return (
          <Snackbar
            key={deletedStudent.deleteKey}
            message="Student's details deleted"
            views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
            buttonText={'Undo'}
            onTextClick={() => onUndoDeleteStudent(deletedStudent.deleteKey!)}
            states={'delete'}
            className={'fixed center-horizontal top-[80px]'}
          />
        );
      })}
    </>
  );
};

export default StudentForm;
